import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import messagingDashboardApi from '../api';

/**
 * ACTION TYPES
 */
export const FETCH_MESSAGING_DATA = 'messaging/FETCH_MESSAGING_DATA';
export const FETCH_EMAIL_LOGS = 'messaging/FETCH_EMAIL_LOGS';
export const SET_EMAIL_LOGS_PAGINATION = 'messaging/SET_EMAIL_LOGS_PAGINATION';
export const SET_EMAIL_LOGS_FILTER = 'messaging/SET_EMAIL_LOGS_FILTER';

export const FETCH_SMS_LOGS = 'messaging/FETCH_SMS_LOGS';
export const SET_SMS_LOGS_PAGINATION = 'messaging/SET_SMS_LOGS_PAGINATION';
export const SET_SMS_LOGS_FILTER = 'messaging/SET_SMS_LOGS_FILTER';

export const FETCH_EMAIL_LOG = 'messaging/FETCH_EMAIL_LOG';

export const FETCH_HOLIDAYS = 'messaging/FETCH_HOLIDAYS';
export const SET_HOLIDAYS_PAGINATION = 'messaging/SET_HOLIDAYS_PAGINATION';
export const SET_HOLIDAYS_FILTER = 'messaging/SET_HOLIDAYS_FILTER';
export const ADD_HOLIDAY = 'messaging/ADD_HOLIDAY'
export const DELETE_HOLIDAY = 'messaging/DELETE_HOLIDAY'

/**
 * ACTIONS
 */
export const fetchMessagingDataActions = createApiActionCreators(FETCH_MESSAGING_DATA);
export const fetchEmailLogsActions = createApiActionCreators(FETCH_EMAIL_LOGS);
export const setEmailLogsPaginationAction = createActionCreator(SET_EMAIL_LOGS_PAGINATION);
export const setEmailLogsFilterAction = createActionCreator(SET_EMAIL_LOGS_FILTER);

export const fetchSmsLogsActions = createApiActionCreators(FETCH_SMS_LOGS);
export const setSmsLogsPaginationAction = createActionCreator(SET_SMS_LOGS_PAGINATION);
export const setSmsLogsFilterAction = createActionCreator(SET_SMS_LOGS_FILTER);

export const fetchEmailLogActions = createApiActionCreators(FETCH_EMAIL_LOG);

export const fetchHolidaysActions = createApiActionCreators(FETCH_HOLIDAYS);
export const setHolidaysPaginationAction = createActionCreator(SET_HOLIDAYS_PAGINATION);
export const setHolidaysFilterAction = createActionCreator(SET_HOLIDAYS_FILTER);
export const addHolidayAction = createApiActionCreators(ADD_HOLIDAY);
export const deleteHolidayAction = createApiActionCreators(DELETE_HOLIDAY);

/**
 * REDUCERS
 */
const initialState = {
  messagingData: null,
  emailLogs: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      sentDateStart: null,
      sentDateEnd: null,
      search: '',
    },
  },
  smsLogs: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      sentDateStart: null,
      sentDateEnd: null,
      search: '',
    },
  },
  emailLog: null,
  holidays: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      sentDateStart: null,
      sentDateEnd: null,
      search: '',
    },
  }

};

const messagingData = createReducer(initialState.messagingData, {

  [FETCH_MESSAGING_DATA]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null,
  }
});

const emailLogs = createReducer(initialState.emailLogs, {

  [FETCH_EMAIL_LOGS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_EMAIL_LOGS_PAGINATION]: (state, payload) => {
    return ({
      ...state,
      pagination: {
        current: payload.pageNumber + 1,
        pageSize: payload.pageSize,
        total: payload.total,
      },
    })
  },
  [SET_EMAIL_LOGS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
});

const smsLogs = createReducer(initialState.smsLogs, {

  [FETCH_SMS_LOGS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_SMS_LOGS_PAGINATION]: (state, payload) => {
    return ({
      ...state,
      pagination: {
        current: payload.pageNumber + 1,
        pageSize: payload.pageSize,
        total: payload.total,
      },
    })
  },
  [SET_SMS_LOGS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
});

const emailLog = createReducer(initialState.emailLog, {

  [FETCH_EMAIL_LOG]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null
  }
});

const holidays = createReducer(initialState.holidays, {

  [FETCH_HOLIDAYS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_HOLIDAYS_PAGINATION]: (state, payload) => {
    return ({
      ...state,
      pagination: {
        current: payload.pageNumber + 1,
        pageSize: payload.pageSize,
        total: payload.total,
      },
    })
  },
  [SET_HOLIDAYS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
  [ADD_HOLIDAY]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: [...state.items, payload],
    }),
  },
  [DELETE_HOLIDAY]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: state.items.filter(item => item.id != payload),
    }),
  },
});

export default combineReducers({
  messagingData,
  emailLogs,
  smsLogs,
  emailLog,
  holidays
});

/**
 * SELECTORS
 */
export const selectMessagingDashboardState = state => state.messaging
export const selectMessagingData = state => selectMessagingDashboardState(state).messagingData;

export const selectEmailLogsState = state => selectMessagingDashboardState(state).emailLogs;
export const selectEmailLogs = state => selectEmailLogsState(state).items;
export const selectEmailLogsPagination = state => selectEmailLogsState(state).pagination;
export const selectEmailLogsFilter = state => selectEmailLogsState(state).filter;

export const selectSmsLogsState = state => selectMessagingDashboardState(state).smsLogs;
export const selectSmsLogs = state => selectSmsLogsState(state).items;
export const selectSmsLogsPagination = state => selectSmsLogsState(state).pagination;
export const selectSmsLogsFilter = state => selectSmsLogsState(state).filter;

export const selectEmailLog = state => selectMessagingDashboardState(state).emailLog;

export const selectHolidaysState = state => selectMessagingDashboardState(state).holidays;
export const selectHolidays = state => selectHolidaysState(state).items;
export const selectHolidaysPagination = state => selectHolidaysState(state).pagination;
export const selectHolidaysFilter = state => selectHolidaysState(state).filter;





/**
 * SAGAS
 */
function* fetchMessagingData() {

  const resp = yield call(messagingDashboardApi.fetchMessagingData);

  if (resp.ok) {
    yield put(fetchMessagingDataActions.success(resp.data));
  } else {
    yield put(fetchMessagingDataActions.failure(resp.error));
  }
}

function* fetchEmailLogs({ payload }) {

  const resp = yield call(messagingDashboardApi.fetchEmailLogs, payload);



  if (resp.ok) {
    yield put(fetchEmailLogsActions.success(resp.data));
    yield put(setEmailLogsPaginationAction(resp.data));

  } else {
    yield put(fetchEmailLogsActions.failure(resp.error));
  }
}

function* fetchSmsLogs({ payload }) {

  const resp = yield call(messagingDashboardApi.fetchSmsLogs, payload);

  if (resp.ok) {
    yield put(fetchSmsLogsActions.success(resp.data));
    yield put(setSmsLogsPaginationAction(resp.data));
  } else {
    yield put(fetchSmsLogsActions.failure(resp.error));
  }
}

function* fetchEmailLog({ payload }) {

  const resp = yield call(messagingDashboardApi.fetchEmailLog, payload.emailLogId);

  if (resp.ok) {
    yield put(fetchEmailLogActions.success(resp.data));

  } else {
    yield put(fetchEmailLogActions.failure(resp.error));
  }
}

function* fetchHolidays({ payload }) {

  const resp = yield call(messagingDashboardApi.fetchHolidays, payload);

  if (resp.ok) {
    yield put(fetchHolidaysActions.success(resp.data));
    yield put(setHolidaysPaginationAction(resp.data));

  } else {
    yield put(fetchHolidaysActions.failure(resp.error));
  }
}

function* addHoliday({ payload }) {

  const resp = yield call(messagingDashboardApi.addHoliday, payload);

  if (resp.ok) {
    yield put(addHolidayAction.success(resp.data));
  } else {
    yield put(addHolidayAction.failure(resp.error));
  }
}

function* deleteHoliday({ payload }) {

  const resp = yield call(messagingDashboardApi.deleteHoliday, payload);

  if (resp.ok) {
    yield put(deleteHolidayAction.success(resp.data));
  } else {
    yield put(deleteHolidayAction.failure(resp.error));
  }
}


export function* messagingSaga() {
  yield takeLatest(createActionType(FETCH_MESSAGING_DATA, REQUEST), fetchMessagingData);

  yield takeLatest(createActionType(FETCH_EMAIL_LOGS, REQUEST), fetchEmailLogs);
  yield takeLatest(SET_EMAIL_LOGS_FILTER, fetchEmailLogs);

  yield takeLatest(createActionType(FETCH_SMS_LOGS, REQUEST), fetchSmsLogs);
  yield takeLatest(SET_SMS_LOGS_FILTER, fetchSmsLogs);

  yield takeLatest(createActionType(FETCH_EMAIL_LOG, REQUEST), fetchEmailLog);


  yield takeLatest(createActionType(FETCH_HOLIDAYS, REQUEST), fetchHolidays);
  yield takeLatest(SET_HOLIDAYS_FILTER, fetchHolidays);
  yield takeLatest(createActionType(ADD_HOLIDAY, REQUEST), addHoliday);
  yield takeLatest(createActionType(DELETE_HOLIDAY, REQUEST), deleteHoliday);

}



// WEBPACK FOOTER //
// ./src/features/messaging-dashboard/ducks/index.js